"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Modal } from "@components/atoms/Modal";
import WordExpandableText from "@components/atoms/WordExpandableText";
import { useIsMobile } from "@hooks/use-mobile";
import { CarValuationSeoCard } from "@interfaces/carValuationSeo.types";
import assetLoader from "@utils/internalImageLoder";
import styles from "./CarValuationCarouselSection.module.scss";

interface CarValuationCarouselSectionProps {
  title: string;
  items: CarValuationSeoCard[];
  variant: "feature" | "factor" | "showcase";
  factorCardTone?: "surface" | "white" | "dark";
  theme?: "light" | "dark";
  description?: string;
  descriptionWordLimit?: number | null;
  counterTone?: "surface" | "white";
}

const DESKTOP_VISIBLE_COUNT = 4;
const MOBILE_VISIBLE_COUNT = 1;
const MOBILE_FACTOR_WORD_LIMIT = 28;
const DESKTOP_FACTOR_WORD_LIMIT = 34;
const MIN_FACTOR_WORD_LIMIT = 12;

const getWordCount = (text: string) =>
  text
    .trim()
    .split(/\s+/)
    .filter(Boolean).length;

const getExcerpt = (text: string, wordLimit: number) => {
  const words = text.trim().split(/\s+/).filter(Boolean);
  if (words.length <= wordLimit) return text;

  return `${words.slice(0, wordLimit).join(" ")}...`;
};

export default function CarValuationCarouselSection({
  title,
  items,
  variant,
  factorCardTone = "surface",
  theme,
  description,
  descriptionWordLimit,
  counterTone = "surface",
}: CarValuationCarouselSectionProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const isMobile = useIsMobile();
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const scrollTargetIndexRef = useRef<number | null>(null);
  const scrollReleaseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [startIndex, setStartIndex] = useState(0);
  const [activeModalIndex, setActiveModalIndex] = useState<number | null>(null);
  const resolvedTheme = theme || (variant === "feature" ? "dark" : "light");

  const visibleCount = isMobile ? MOBILE_VISIBLE_COUNT : DESKTOP_VISIBLE_COUNT;
  const canScroll = items.length > visibleCount;
  const maxStartIndex = Math.max(0, items.length - visibleCount);
  const displayCount = Math.min(startIndex + visibleCount, items.length);
  const resolvedSectionDescriptionWordLimit =
    descriptionWordLimit ?? Number.MAX_SAFE_INTEGER;

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const syncIndexFromScroll = () => {
    if (!scrollerRef.current || itemRefs.current.length === 0) return;

    if (!isMobile && scrollTargetIndexRef.current !== null) {
      setStartIndex(scrollTargetIndexRef.current);
      return;
    }

    const scrollLeft = scrollerRef.current.scrollLeft;
    let closestIndex = 0;
    let closestDistance = Number.POSITIVE_INFINITY;

    itemRefs.current.forEach((item, index) => {
      if (!item) return;
      const distance = Math.abs(item.offsetLeft - scrollLeft);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestIndex = index;
      }
    });

    const normalizedIndex = Math.min(
      isMobile ? closestIndex : closestIndex,
      maxStartIndex
    );

    setStartIndex(normalizedIndex);
  };

  useEffect(() => {
    syncIndexFromScroll();
  }, [isMobile]);

  useEffect(() => {
    return () => {
      if (scrollReleaseTimerRef.current) {
        clearTimeout(scrollReleaseTimerRef.current);
      }
    };
  }, []);

  const scrollToIndex = (index: number) => {
    const safeIndex = Math.max(0, Math.min(index, maxStartIndex));
    const target = itemRefs.current[safeIndex];

    if (!target) return;

    if (!isMobile) {
      scrollTargetIndexRef.current = safeIndex;
      if (scrollReleaseTimerRef.current) {
        clearTimeout(scrollReleaseTimerRef.current);
      }
      scrollReleaseTimerRef.current = setTimeout(() => {
        scrollTargetIndexRef.current = null;
        syncIndexFromScroll();
      }, 450);
    }

    target.scrollIntoView({
      behavior: "smooth",
      inline: "start",
      block: "nearest",
    });

    setStartIndex(safeIndex);
  };

  const handleNext = () => {
    const step = isMobile ? 1 : DESKTOP_VISIBLE_COUNT;
    scrollToIndex(Math.min(startIndex + step, maxStartIndex));
  };

  const handlePrev = () => {
    const step = isMobile ? 1 : DESKTOP_VISIBLE_COUNT;
    scrollToIndex(Math.max(startIndex - step, 0));
  };

  const renderReadMore = (item: CarValuationSeoCard, index: number) => {
    const defaultWordLimit = isMobile
      ? MOBILE_FACTOR_WORD_LIMIT
      : DESKTOP_FACTOR_WORD_LIMIT;
    const resolvedDescriptionLimit =
      item.descriptionWordLimit && item.descriptionWordLimit > 0
        ? Math.max(item.descriptionWordLimit, MIN_FACTOR_WORD_LIMIT)
        : defaultWordLimit;
    const needsReadMore =
      variant === "factor" &&
      getWordCount(item.description) > resolvedDescriptionLimit;

    if (!needsReadMore) {
      return (
        <p className={styles.cardDescription}>
          {item.description}
        </p>
      );
    }

    return (
      <p className={styles.cardDescription}>
        {getExcerpt(item.description, resolvedDescriptionLimit)}{" "}
        <button
          type="button"
          className={styles.readMoreButton}
          onClick={() => setActiveModalIndex(index)}
        >
          Read more
        </button>
      </p>
    );
  };

  return (
    <>
      <div
        className={`${styles.section} ${
          resolvedTheme === "dark" ? styles.darkTheme : styles.lightTheme
        }`}
      >
        <div className={styles.headerRow}>
          <div className={styles.headingBlock}>
            <h2 className={styles.heading}>{title}</h2>
            {description ? (
              <WordExpandableText
                text={description}
                wordLimit={resolvedSectionDescriptionWordLimit}
                mobileOnly
                className={styles.description}
                buttonClassName={styles.readMoreButton}
              />
            ) : null}
          </div>
          {isHydrated && !isMobile && canScroll ? (
            <div className={styles.controls}>
              <div
                className={`${styles.counter} ${
                  counterTone === "white" ? styles.counterWhite : ""
                }`}
              >
                {`${displayCount}/${items.length}`}
              </div>
              <div className={styles.buttonRow}>
                <button
                  type="button"
                  className={`${styles.navButton} ${styles.prevButton}`}
                  onClick={handlePrev}
                  disabled={startIndex === 0}
                  aria-label="Previous cards"
                >
                  <Image
                    src={`${assetLoader()}/icons/Carousal/${resolvedTheme === "dark" ? "ArrowLeftDark" : "ArrowLeft"}.svg`}
                    alt=""
                    width={20}
                    height={20}
                    className={styles.arrowIcon}
                    aria-hidden="true"
                  />
                </button>
                <button
                  type="button"
                  className={`${styles.navButton} ${styles.nextButton}`}
                  onClick={handleNext}
                  disabled={startIndex >= maxStartIndex}
                  aria-label="Next cards"
                >
                  <Image
                    src={`${assetLoader()}/icons/Carousal/${resolvedTheme === "dark" ? "ArrowRightDark" : "ArrowRight"}.svg`}
                    alt=""
                    width={20}
                    height={20}
                    className={styles.arrowIcon}
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          ) : null}
        </div>

        <div
          ref={scrollerRef}
          className={styles.scroller}
          onScroll={syncIndexFromScroll}
        >
          {items.map((item, index) => (
            <div
              key={`${item.title}-${index}`}
              ref={(node) => {
                itemRefs.current[index] = node;
              }}
              className={`${styles.card} ${
                variant === "feature"
                  ? styles.featureCard
                  : variant === "showcase"
                    ? styles.showcaseCard
                    : `${styles.factorCard} ${
                      factorCardTone === "white"
                        ? styles.factorCardWhite
                        : factorCardTone === "dark"
                          ? styles.factorCardDark
                        : styles.factorCardSurface
                    }`
              }`}
            >
              {variant === "feature" ? (
                <>
                  {item.image?.url ? (
                    <div className={styles.featureImageWrap}>
                      <Image
                        src={item.image.url}
                        alt={item.image.alt}
                        fill
                        sizes="(max-width: 767px) 242px, 282px"
                        className={styles.featureImage}
                      />
                    </div>
                  ) : null}
                  <div className={styles.featureBody}>
                    <h3 className={styles.featureTitle}>{item.title}</h3>
                    <p className={styles.featureDescription}>{item.description}</p>
                  </div>
                </>
              ) : variant === "showcase" ? (
                <>
                  {item.image?.url ? (
                    <div className={styles.showcaseImageWrap}>
                      <Image
                        src={item.image.url}
                        alt={item.image.alt}
                        fill
                        sizes="(max-width: 767px) 280px, 282px"
                        className={styles.showcaseImage}
                      />
                    </div>
                  ) : null}
                  <div className={styles.showcaseBody}>
                    <h3 className={styles.showcaseTitle}>{item.title}</h3>
                    {item.description ? (
                      <p className={styles.showcaseDescription}>{item.description}</p>
                    ) : null}
                  </div>
                </>
              ) : (
                <>
                  {item.image?.url ? (
                    <div className={styles.factorIconWrap}>
                      <Image
                        src={item.image.url}
                        alt={item.image.alt}
                        width={isMobile ? 32 : 48}
                        height={isMobile ? 32 : 48}
                        className={styles.factorIcon}
                      />
                    </div>
                  ) : null}
                  <div className={styles.factorBody}>
                    <h3 className={styles.factorTitle}>{item.title}</h3>
                    {isHydrated ? (
                      renderReadMore(item, index)
                    ) : (
                      <p className={styles.cardDescription}>{item.description}</p>
                    )}
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {isHydrated && variant === "factor" && activeModalIndex !== null ? (
        <Modal
          isOpen={activeModalIndex !== null}
          onClose={() => setActiveModalIndex(null)}
          showCloseButton={true}
          overlayClassName={styles.modalOverlay}
          closeButtonClassName={styles.modalCloseButton}
          contentClassName={styles.modalContentClass}
          bodyClassName={styles.modalBodyClass}
          headerClassName={styles.headerClassName}
        >
            <div className={styles.modalTopRow}>
              {items[activeModalIndex]?.image?.url ? (
                <Image
                  src={items[activeModalIndex].image!.url}
                  alt={items[activeModalIndex].image!.alt}
                  width={40}
                  height={40}
                />
              ) : (
                <div className={styles.modalIconPlaceholder} />
              )}
            </div>
            <div className={styles.modalTextBlock}>
              <h3 className={styles.modalTitle}>{items[activeModalIndex]?.title}</h3>
              <p className={styles.modalDescription}>
                {items[activeModalIndex]?.description}
              </p>
            </div>
            <button
              type="button"
              className={styles.modalCta}
              onClick={() => setActiveModalIndex(null)}
            >
              Understood
            </button>
        </Modal>
      ) : null}
    </>
  );
}
